// Recon policies, IP ranges, network zones, recon scans, exposure monitoring API
import { api } from './client';

// ============================================================================
// RECON POLICY TYPES
// ============================================================================

export interface ReconPolicy {
  id: number;
  name: string;
  description: string;
  is_system: boolean;
  is_default: boolean;

  // Data source toggles
  use_shodan: boolean;
  use_censys: boolean;
  use_virustotal: boolean;
  use_crtsh: boolean;
  use_dns_enum: boolean;
  use_wayback: boolean;
  use_breach_data: boolean;
  use_github_search: boolean;

  // Shodan options
  shodan_historical: boolean;
  shodan_vuln_check: boolean;
  shodan_netblocks: boolean;
  shodan_credit_limit: number;

  // DNS options
  dns_zone_transfer: boolean;
  dns_subdomain_enum: boolean;
  dns_record_types: string[];

  // Breach data options
  breach_email_search: boolean;
  breach_domain_search: boolean;
  breach_max_results: number;

  // Detection features
  detect_shadow_it: boolean;
  detect_cloud_assets: boolean;
  detect_exposed_creds: boolean;
  detect_misconfigs: boolean;

  // Risk amplification
  enable_risk_amplification: boolean;
  amplification_exposure_weight: number;
  amplification_exploit_weight: number;
  amplification_kev_weight: number;

  // Rate limiting
  rate_limit_requests_per_second: number;
  max_concurrent_lookups: number;

  // Reporting
  include_raw_data: boolean;
  include_historical_data: boolean;
  generate_timeline: boolean;

  created_at?: string;
  updated_at?: string;
  created_by?: number;
}

export interface CreateReconPolicyRequest {
  name: string;
  description?: string;
  is_default?: boolean;
  use_shodan?: boolean;
  use_censys?: boolean;
  use_virustotal?: boolean;
  use_crtsh?: boolean;
  use_dns_enum?: boolean;
  use_wayback?: boolean;
  use_breach_data?: boolean;
  use_github_search?: boolean;
  shodan_historical?: boolean;
  shodan_vuln_check?: boolean;
  shodan_netblocks?: boolean;
  shodan_credit_limit?: number;
  dns_zone_transfer?: boolean;
  dns_subdomain_enum?: boolean;
  dns_record_types?: string[];
  breach_email_search?: boolean;
  breach_domain_search?: boolean;
  breach_max_results?: number;
  detect_shadow_it?: boolean;
  detect_cloud_assets?: boolean;
  detect_exposed_creds?: boolean;
  detect_misconfigs?: boolean;
  enable_risk_amplification?: boolean;
  amplification_exposure_weight?: number;
  amplification_exploit_weight?: number;
  amplification_kev_weight?: number;
  rate_limit_requests_per_second?: number;
  max_concurrent_lookups?: number;
  include_raw_data?: boolean;
  include_historical_data?: boolean;
  generate_timeline?: boolean;
}

// ============================================================================
// IP RANGE & NETWORK ZONE TYPES
// ============================================================================

export interface NetworkZone {
  id: number;
  project_id: number;
  name: string;
  description: string;
  color: string;
  is_default: boolean;
  range_count?: number;
  target_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateNetworkZoneRequest {
  name: string;
  description?: string;
  color?: string;
  is_default?: boolean;
}

export interface IPRange {
  id: number;
  project_id: number;
  name: string;
  range_value: string;
  range_type: 'cidr' | 'range';
  start_ip: string;
  end_ip: string;
  total_ips: number;
  zone_id?: number;
  zone?: NetworkZone;
  description: string;
  is_active: boolean;
  target_count?: number;
  alive_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateIPRangeRequest {
  name?: string;
  range_value: string;
  range_type: 'cidr' | 'range';
  zone_id?: number;
  description?: string;
  is_active?: boolean;
}

// ============================================================================
// RECON POLICY API
// ============================================================================

export const reconPolicyApi = {
  // Get all recon policies
  getPolicies: () => {
    console.log('📋 Fetching recon policies');
    return api.get<ReconPolicy[]>('/recon/policies');
  },

  // Get default policy
  getDefaultPolicy: () => {
    console.log('📋 Fetching default recon policy');
    return api.get<ReconPolicy>('/recon/policies/default');
  },

  // Get a specific policy
  getPolicy: (id: number) => {
    console.log('📋 Fetching recon policy:', id);
    return api.get<ReconPolicy>(`/recon/policies/${id}`);
  },

  // Create a new policy
  createPolicy: (policy: CreateReconPolicyRequest) => {
    console.log('➕ Creating recon policy:', policy.name);
    return api.post<ReconPolicy>('/recon/policies', policy);
  },

  // Update a policy
  updatePolicy: (id: number, policy: Partial<CreateReconPolicyRequest>) => {
    console.log('✏️ Updating recon policy:', id);
    return api.put<ReconPolicy>(`/recon/policies/${id}`, policy);
  },

  // Delete a policy
  deletePolicy: (id: number) => {
    console.log('🗑️ Deleting recon policy:', id);
    return api.delete(`/recon/policies/${id}`);
  },

  // Duplicate a policy
  duplicatePolicy: (id: number, newName: string) => {
    console.log('📋 Duplicating recon policy:', id, 'as', newName);
    return api.post<ReconPolicy>(`/recon/policies/${id}/duplicate`, { name: newName });
  },

  // Set a policy as default
  setDefaultPolicy: (id: number) => {
    console.log('⭐ Setting default recon policy:', id);
    return api.post<ReconPolicy>(`/recon/policies/${id}/set-default`);
  },
};

// ============================================================================
// IP RANGES API
// ============================================================================

export const ipRangesAPI = {
  // Get all IP ranges for a project
  getByProject: (projectId: number) => {
    console.log('📡 Fetching IP ranges for project:', projectId);
    return api.get<IPRange[]>(`/project/${projectId}/ip-ranges`);
  },

  // Get a specific IP range
  getById: (projectId: number, rangeId: number) => {
    console.log('📡 Fetching IP range:', rangeId);
    return api.get<IPRange>(`/project/${projectId}/ip-ranges/${rangeId}`);
  },

  // Create a new IP range
  create: (projectId: number, data: CreateIPRangeRequest) => {
    console.log('➕ Creating IP range:', data.range_value);
    return api.post<IPRange>(`/project/${projectId}/ip-ranges`, data);
  },

  // Update an IP range
  update: (projectId: number, rangeId: number, data: Partial<CreateIPRangeRequest>) => {
    console.log('✏️ Updating IP range:', rangeId);
    return api.put<IPRange>(`/project/${projectId}/ip-ranges/${rangeId}`, data);
  },

  // Delete an IP range
  delete: (projectId: number, rangeId: number) => {
    console.log('🗑️ Deleting IP range:', rangeId);
    return api.delete(`/project/${projectId}/ip-ranges/${rangeId}`);
  },

  // Get targets within a range
  getTargets: (projectId: number, rangeId: number) => {
    console.log('🎯 Fetching targets in IP range:', rangeId);
    return api.get(`/project/${projectId}/ip-ranges/${rangeId}/targets`);
  },

  // Link targets to a range
  linkTargets: (projectId: number, rangeId: number) => {
    console.log('🔗 Linking targets to IP range:', rangeId);
    return api.post(`/project/${projectId}/ip-ranges/${rangeId}/link-targets`);
  },
};

// ============================================================================
// NETWORK ZONES API
// ============================================================================

export const networkZonesAPI = {
  // Get all network zones for a project
  getByProject: (projectId: number) => {
    console.log('🏷️ Fetching network zones for project:', projectId);
    return api.get<NetworkZone[]>(`/project/${projectId}/zones`);
  },

  // Get a specific network zone
  getById: (projectId: number, zoneId: number) => {
    console.log('🏷️ Fetching network zone:', zoneId);
    return api.get<NetworkZone>(`/project/${projectId}/zones/${zoneId}`);
  },

  // Create a new network zone
  create: (projectId: number, data: CreateNetworkZoneRequest) => {
    console.log('➕ Creating network zone:', data.name);
    return api.post<NetworkZone>(`/project/${projectId}/zones`, data);
  },

  // Update a network zone
  update: (projectId: number, zoneId: number, data: Partial<CreateNetworkZoneRequest>) => {
    console.log('✏️ Updating network zone:', zoneId);
    return api.put<NetworkZone>(`/project/${projectId}/zones/${zoneId}`, data);
  },

  // Delete a network zone
  delete: (projectId: number, zoneId: number) => {
    console.log('🗑️ Deleting network zone:', zoneId);
    return api.delete(`/project/${projectId}/zones/${zoneId}`);
  },

  // Get all IP ranges in a zone
  getRanges: (projectId: number, zoneId: number) => {
    console.log('📡 Fetching IP ranges in zone:', zoneId);
    return api.get<IPRange[]>(`/project/${projectId}/zones/${zoneId}/ranges`);
  },
};

// ============================================================================
// RECON SCANS API
// ============================================================================

export const reconScansAPI = {
  // Get all recon scans
  getAll: (params?: { page?: number; limit?: number; project_id?: number }) => {
    console.log('🔍 Fetching recon scans');
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.project_id) searchParams.append('project_id', params.project_id.toString());
    const queryString = searchParams.toString();
    return api.get(`/recon/scans${queryString ? `?${queryString}` : ''}`);
  },

  // Get a specific recon scan
  getById: (id: number) => {
    console.log('🔍 Fetching recon scan:', id);
    return api.get(`/recon/scans/${id}`);
  },

  // Export recon scan as HTML report
  exportHTML: (id: number, language?: string) => {
    console.log('📄 Exporting HTML report for recon scan:', id);
    const params = new URLSearchParams();
    if (language) params.append('language', language);
    const queryString = params.toString();
    return api.get(`/recon/scans/${id}/export/html${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob',
      timeout: 60000, // 60 seconds timeout
      headers: {
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
      }
    });
  },

  // Export recon scan as PDF report
  exportPDF: (id: number) => {
    console.log('📄 Exporting PDF report for recon scan:', id);
    return api.get<{ download_url: string }>(`/recon/scans/${id}/export/pdf`, {
      timeout: 120000, // 2 minutes timeout for PDF generation
    });
  },

  // Export recon scan as DOCX report
  exportDocx: (id: number) => {
    return api.get<{ download_url: string }>(`/recon/scans/${id}/export/recon-docx`, {
      timeout: 180000,
    });
  },

  // Export the recon IP-ownership classification as a multi-sheet Excel workbook
  exportXLSX: (id: number, language?: string) => {
    const q = language ? `?language=${language}` : '';
    return api.get(`/recon/scans/${id}/export/xlsx${q}`, { responseType: 'blob', timeout: 120000 });
  },

  // Cancel a running recon scan
  cancel: (id: number) => {
    console.log('🛑 Cancelling recon scan:', id);
    return api.post(`/recon/scans/${id}/cancel`);
  },

  // Get IP netblocks grouped by ownership (own / own_isp_ip / saas / external)
  getNetblocks: (id: number, language?: string) => {
    const q = language ? `?language=${language}` : '';
    return api.get(`/recon/scans/${id}/netblocks${q}`);
  },

  // Manually reclassify a netblock/ASN/owner as own or external
  reclassifyNetblock: (id: number, body: { target_type: 'asn' | 'cidr' | 'org'; value: string; category: 'own' | 'external' }) => {
    return api.post(`/recon/scans/${id}/netblocks/reclassify`, body);
  },

  // Get recon scan assets
  getAssets: (scanId: number) => {
    console.log('📦 Fetching assets for recon scan:', scanId);
    return api.get(`/recon/scans/${scanId}/assets`);
  },

  // Get recon scan findings
  getFindings: (scanId: number) => {
    console.log('🔎 Fetching findings for recon scan:', scanId);
    return api.get(`/recon/scans/${scanId}/findings`);
  },
};

// ============================================================================
// EXPOSURE MONITORING API
// ============================================================================

export const exposureAPI = {
  // Get exposure snapshots for a project
  getSnapshots: (projectId: number) => {
    return api.get(`/projects/${projectId}/exposure-snapshots`);
  },

  // Create a new exposure snapshot
  createSnapshot: (snapshot: {
    project_id: number;
    name: string;
    description?: string;
    source_scan_id?: number;
    is_baseline?: boolean;
  }) => {
    return api.post('/exposure-snapshots', snapshot);
  },

  // Get exposure events for a project (standard events only)
  getEvents: (projectId: number, params?: {
    event_type?: string;
    acknowledged?: boolean;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.event_type) searchParams.append('event_type', params.event_type);
    if (params?.acknowledged !== undefined) searchParams.append('acknowledged', params.acknowledged.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    const queryString = searchParams.toString();
    return api.get(`/projects/${projectId}/exposure-events${queryString ? '?' + queryString : ''}`);
  },

  // Get all exposure events (combines standard and enhanced events including certificate expiry)
  getAllEvents: (projectId: number, params?: {
    event_type?: string;
    acknowledged?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.event_type) searchParams.append('event_type', params.event_type);
    if (params?.acknowledged !== undefined) searchParams.append('acknowledged', params.acknowledged.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) searchParams.append('offset', params.offset.toString());
    const queryString = searchParams.toString();
    return api.get(`/projects/${projectId}/exposure-events/all${queryString ? '?' + queryString : ''}`);
  },

  // Get enhanced exposure events (certificate expiry, subdomain changes, etc.)
  getEnhancedEvents: (projectId: number, params?: {
    event_type?: string;
    event_category?: string;
    acknowledged?: boolean;
    limit?: number;
    page?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.event_type) searchParams.append('event_type', params.event_type);
    if (params?.event_category) searchParams.append('event_category', params.event_category);
    if (params?.acknowledged !== undefined) searchParams.append('acknowledged', params.acknowledged.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.page) searchParams.append('page', params.page.toString());
    const queryString = searchParams.toString();
    return api.get(`/projects/${projectId}/exposure-events/enhanced${queryString ? '?' + queryString : ''}`);
  },

  // Acknowledge an exposure event
  // source: 'standard' or 'enhanced' to indicate which table the event is from
  acknowledgeEvent: (eventId: number, source?: string) => {
    const params = source ? `?source=${source}` : '';
    return api.post(`/exposure-events/${eventId}/acknowledge${params}`);
  },

  // Bulk acknowledge multiple exposure events
  bulkAcknowledge: (projectId: number, eventIds: number[], source?: string) => {
    return api.post(`/projects/${projectId}/exposure-events/bulk-acknowledge`, {
      event_ids: eventIds,
      source: source || 'all',
    });
  },

  // Acknowledge all unacknowledged exposure events for a project
  acknowledgeAll: (projectId: number, filters?: { event_type?: string; event_category?: string }) => {
    return api.post(`/projects/${projectId}/exposure-events/acknowledge-all`, filters || {});
  },

  // Cleanup old exposure events (admin only)
  cleanup: (projectId: number, options?: {
    older_than_days?: number;
    acknowledged_only?: boolean;
    event_type?: string;
  }) => {
    return api.delete(`/projects/${projectId}/exposure-events/cleanup`, { data: options || {} });
  },

  // Get exposure event statistics (actual DB counts, not affected by filters)
  getEventStats: (projectId: number) => {
    return api.get(`/projects/${projectId}/exposure-events/stats`);
  },

  // Get schedule status overview for exposure monitoring
  // Shows which schedule types (active, recon, breach) are enabled
  getScheduleStatus: (projectId: number) => {
    return api.get<{
      active_scans: {
        enabled: boolean;
        schedule_count: number;
        last_run: string | null;
      };
      recon_scans: {
        enabled: boolean;
        schedule_count: number;
        last_run: string | null;
      };
      breach_monitoring: {
        enabled: boolean;
        system_enabled: boolean;
        project_enabled: boolean;
        domain_count: number;
        last_run: string | null;
      };
    }>(`/projects/${projectId}/exposure-schedule-status`);
  },

  // Delete a single acknowledged exposure event
  deleteEvent: (projectId: number, eventId: number, source?: string) => {
    const params = source ? `?source=${source}` : '';
    return api.delete(`/projects/${projectId}/exposure-events/${eventId}${params}`);
  },

  // Delete multiple acknowledged exposure events
  deleteAcknowledgedEvents: (projectId: number, eventIds?: number[]) => {
    return api.delete(`/projects/${projectId}/exposure-events/acknowledged`, {
      data: eventIds ? { event_ids: eventIds } : {}
    });
  },

  // Get audit logs for exposure events
  getAuditLogs: (projectId: number, options?: { page?: number; limit?: number; action?: string }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.action) params.append('action', options.action);
    const queryString = params.toString();
    return api.get<{
      audit_logs: Array<{
        id: number;
        project_id: number;
        user_id: number;
        username: string;
        action: string;
        event_id: number | null;
        event_type: string;
        event_count: number;
        details: string;
        ip_address: string;
        user_agent: string;
        created_at: string;
      }>;
      total: number;
      page: number;
      limit: number;
      pages: number;
    }>(`/projects/${projectId}/exposure-events/audit-logs${queryString ? '?' + queryString : ''}`);
  },

  // Compare two scans
  compareScans: (scan1Id: number, scan2Id: number) => {
    return api.post('/scans/compare', { scan1_id: scan1Id, scan2_id: scan2Id });
  },
};
